import { useState } from "react";
import { Plus, Trash2, Calendar, Users, AlertCircle } from "lucide-react";

export interface TripDate {
  id?: string;
  startDate: string;
  endDate: string;
  price: number;
  capacity: number;
  bookedCount?: number;
  status: "available" | "few_spots" | "sold_out" | "cancelled";
  guideId?: string;
  notes?: string;
}

interface Props {
  value: TripDate[];
  onChange: (value: TripDate[]) => void;
  guides?: { id: string; name: string }[];
}

const STATUS_OPTIONS = [
  { value: "available", label: "Volný", color: "bg-success-100 text-success-700" },
  { value: "few_spots", label: "Posledních pár míst", color: "bg-warning-100 text-warning-700" },
  { value: "sold_out", label: "Vyprodáno", color: "bg-error-100 text-error-700" },
  { value: "cancelled", label: "Zrušeno", color: "bg-gray-100 text-gray-700" },
];

export default function TripDatesEditor({ value, onChange, guides = [] }: Props) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const addDate = () => {
    const today = new Date();
    const startDate = new Date(today.setMonth(today.getMonth() + 3));
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 14);

    const newDate: TripDate = {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
      price: 0,
      capacity: 20,
      bookedCount: 0,
      status: "available",
    };
    onChange([...value, newDate]);
    setExpandedIndex(value.length);
  };

  const updateDate = (index: number, updates: Partial<TripDate>) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], ...updates };
    onChange(newValue);
  };

  const removeDate = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
    if (expandedIndex === index) {
      setExpandedIndex(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("cs-CZ", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const calculateDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  const getStatusInfo = (status: string) => {
    return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Termíny ({value.length})
          </h3>
        </div>
        <button type="button" onClick={addDate} className="btn-ghost btn-sm text-primary-600">
          <Plus className="h-4 w-4" />
          Přidat termín
        </button>
      </div>

      {value.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">Zatím žádné termíny</p>
          <button type="button" onClick={addDate} className="btn-primary btn-sm mt-4">
            <Plus className="h-4 w-4" />
            Přidat první termín
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {value.map((date, index) => {
            const isExpanded = expandedIndex === index;
            const status = getStatusInfo(date.status);
            const duration = calculateDuration(date.startDate, date.endDate);
            const spotsLeft = date.capacity - (date.bookedCount || 0);

            return (
              <div
                key={index}
                className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
              >
                {/* Header */}
                <div
                  className="flex cursor-pointer items-center gap-4 p-4"
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatDate(date.startDate)} - {formatDate(date.endDate)}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                      <span>{duration} dní</span>
                      <span className="flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" />
                        {spotsLeft}/{date.capacity} míst
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {date.price.toLocaleString("cs-CZ")} Kč
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDate(index);
                    }}
                    className="p-1 text-error-500 hover:text-error-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Content */}
                {isExpanded && (
                  <div className="space-y-4 border-t border-gray-200 p-4 dark:border-gray-700">
                    <div className="grid gap-4 sm:grid-cols-2">
                      {/* Start Date */}
                      <div>
                        <label className="label">Datum odjezdu</label>
                        <input
                          type="date"
                          value={date.startDate}
                          onChange={(e) => updateDate(index, { startDate: e.target.value })}
                          className="input"
                        />
                      </div>

                      {/* End Date */}
                      <div>
                        <label className="label">Datum návratu</label>
                        <input
                          type="date"
                          value={date.endDate}
                          onChange={(e) => updateDate(index, { endDate: e.target.value })}
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-3">
                      {/* Price */}
                      <div>
                        <label className="label">Cena (Kč)</label>
                        <input
                          type="number"
                          value={date.price}
                          onChange={(e) => updateDate(index, { price: parseInt(e.target.value) || 0 })}
                          min={0}
                          step={1000}
                          className="input"
                        />
                      </div>

                      {/* Capacity */}
                      <div>
                        <label className="label">Kapacita</label>
                        <input
                          type="number"
                          value={date.capacity}
                          onChange={(e) => updateDate(index, { capacity: parseInt(e.target.value) || 0 })}
                          min={1}
                          className="input"
                        />
                      </div>

                      {/* Booked Count */}
                      <div>
                        <label className="label">Obsazeno</label>
                        <input
                          type="number"
                          value={date.bookedCount || 0}
                          onChange={(e) => updateDate(index, { bookedCount: parseInt(e.target.value) || 0 })}
                          min={0}
                          max={date.capacity}
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      {/* Status */}
                      <div>
                        <label className="label">Stav</label>
                        <select
                          value={date.status}
                          onChange={(e) => updateDate(index, { status: e.target.value as TripDate["status"] })}
                          className="input"
                        >
                          {STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Guide */}
                      <div>
                        <label className="label">Průvodce</label>
                        <select
                          value={date.guideId || ""}
                          onChange={(e) => updateDate(index, { guideId: e.target.value || undefined })}
                          className="input"
                        >
                          <option value="">Vyberte průvodce...</option>
                          {guides.map((guide) => (
                            <option key={guide.id} value={guide.id}>
                              {guide.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="label">Poznámky (interní)</label>
                      <textarea
                        value={date.notes || ""}
                        onChange={(e) => updateDate(index, { notes: e.target.value })}
                        rows={2}
                        placeholder="Interní poznámky k termínu..."
                        className="input"
                      />
                    </div>

                    {/* Warning for low spots */}
                    {spotsLeft > 0 && spotsLeft <= 5 && (
                      <div className="flex items-center gap-2 rounded-lg bg-warning-50 p-3 text-sm text-warning-700 dark:bg-warning-900/20 dark:text-warning-400">
                        <AlertCircle className="h-4 w-4" />
                        Zbývá pouze {spotsLeft} volných míst!
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
